import React, { useContext, useEffect, useMemo, useState } from 'react';
import Modal from '~/components/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '~/store';
import ExpenseForm from './UpdateExpenseForm';
import { ExpensesPageContext } from '~/pages/ExpensesDeprecated/context/ExpensesContext';
import toast from 'react-hot-toast';
import request from '~/utils/request';
import { ZExpense } from '~/pages/ExpensesDeprecated/entity/schemas';
import { z } from 'zod';
import { IExpense } from '../types';
import useFormulaContext from '~/components/Formulas/context/useFormulaContext';
import { IBvaReviewItem } from '~/services/parallel/bva.types';
import BvaGraph from '~/components/BvaDropdown/components/BvaGraph';
import DisplayExpenseAverages from './DisplayExpenseAverages';
import updateExpense from './updateExpense';
import * as api from '~/services/parallel';
import { update } from '~/store/bvaSlice';
import Select, { useSelect } from '~/components/Select';
import { IDateValue } from '~/services/parallel/bva.types';
import Typography from '~/components/Typography';

const UpdateExpenseForBvaModal = (): React.ReactNode => {
  const bva = useSelector((state: State) => state.bva);
  const { expenseReview } = bva;

  const dispatch = useDispatch();
  const {
    expenses,
    setExpenses,
    revalidateFilteredExpensesReport,
    revalidateFormulaList,
    reload,
    formulaList,
    variables,
    setVariables,
    displayFormulaError,
    setDisplayFormulaError,
    formula,
    setFormula,
    updatedFormula,
    setUpdatedFormula,
    formulaValue,
    setFormulaValue,
    expenseIntegrationMappings,
    formState,
    updateExpenseForBva,
    setUpdateExpenseForBva,
  } = useContext(ExpensesPageContext);
  const { refreshData } = useFormulaContext();
  const activeScenarioUuid = useSelector((state: State) => state.scenario.activeScenarioUuid);
  const organizationUuid = useSelector((state: State) => state.organization.uuid);

  useEffect(() => {
    const getExpenseDetails = async (): Promise<void> => {
      if (!updateExpenseForBva) return;
      const fetchExpenseResponse = await request({
        url: `/expenses/${updateExpenseForBva.expenseUuid}`,
        headers: { 'Organization-Uuid': organizationUuid },
        params: { scenarioUuid: activeScenarioUuid },
        method: 'GET',
      });
      if (fetchExpenseResponse.status >= 400) return;

      const parsedResponse = z
        .object({
          data: z.object({ data: ZExpense }),
        })
        .parse(fetchExpenseResponse);

      formState.setFormStateFromIExpense({
        expense: parsedResponse.data.data,
        expenseIntegrationMappings,
      });
    };

    if (updateExpenseForBva) {
      getExpenseDetails();
    }
  }, [updateExpenseForBva]);

  useEffect(() => {
    if (updateExpenseForBva) {
      const expenseFormula = formulaList.find(
        (formula) => formula.recipe.relatedResourceUuid === updateExpenseForBva.expenseUuid,
      );
      if (!expenseFormula) return;
      if (formState.type === 'custom') {
        formState.setFormulaInputState((prev) => ({
          ...prev,
          formulaState: {
            ...prev.formulaState,
            formula: expenseFormula.recipe.expression,
            variables: expenseFormula.recipe.variables,
            formulaList: formulaList,
            editable: true,
            topLevelFormulaUuid: expenseFormula.formulaUuid,
          },
          formulaTitle: formState.name.value,
          variables: expenseFormula.recipe.variables,
          isCustom: !!updateExpenseForBva,
          formulaUuid: expenseFormula.formulaUuid,
        }));
      } else {
        formState.setFormulaInputState({
          formulaState: {
            topLevelFormulaUuid: expenseFormula.formulaUuid,
            formula: '',
            variables: {},
            formulaList: formulaList,
            editable: true,
          },
          variables: {},
          isCustom: false,
          formulaUuid: expenseFormula.formulaUuid,
        });
        setFormula([]);
        setVariables({});
        setUpdatedFormula([]);
      }
    }
  }, [updateExpenseForBva, formState.type]);

  const reviewForExpense = useMemo((): IBvaReviewItem | null => {
    if (!updateExpenseForBva) return null;
    const review = expenseReview[updateExpenseForBva.formulaUuid];
    return review;
  }, [updateExpenseForBva, expenseReview]);

  const forecastAsOfOptions = useMemo(
    () =>
      reviewForExpense?.forecastedValues.map(({ monthsAgo }) => ({
        label: monthsAgo === 1 ? 'Last Month' : `${monthsAgo} Months Ago`,
        value: `${monthsAgo}`,
      })),
    [reviewForExpense?.forecastedValues],
  );

  const [forecastAsOf, setForecastAsOf] = useSelect({
    options: forecastAsOfOptions ?? [],
    selected: forecastAsOfOptions?.[0],
  });

  const [forecastValues, setForecastValues] = useState<IDateValue[]>([]);

  useEffect(() => {
    if (reviewForExpense?.forecastedValues && forecastAsOf.selected) {
      const forecastValues = reviewForExpense.forecastedValues.find(
        ({ monthsAgo }) => monthsAgo === Number(forecastAsOf.selected?.value),
      )?.values;

      setForecastValues(forecastValues ?? []);
    }
  }, [reviewForExpense?.forecastedValues, forecastAsOf]);

  useEffect(() => {
    if (forecastAsOfOptions && forecastAsOfOptions.length > 0 && !forecastAsOf.selected?.label) {
      setForecastAsOf((prev) => ({
        ...prev,
        options: forecastAsOfOptions,
        selected: forecastAsOfOptions[0],
      }));
    }
  }, [forecastAsOfOptions, forecastAsOf.selected]);

  const completeReviewForExpense = async (): Promise<void> => {
    if (!updateExpenseForBva) return;
    const updatedBvaResult = await api.bva.completeReview({
      organizationUuid,
      expenseReviewsToComplete: [updateExpenseForBva.formulaUuid],
    });

    if (updatedBvaResult) {
      dispatch(
        update({
          ...bva,
          expenseReview: updatedBvaResult.expenseReview,
        }),
      );
    } else {
      toast.error('Failed to complete expense review');
    }
  };

  const expenseTitle = useMemo(() => {
    if (!updateExpenseForBva) return '';
    const expense = expenses.find((expense) => expense.expenseUuid === updateExpenseForBva.expenseUuid);
    return expense?.name ?? '';
  }, [updateExpenseForBva, expenses]);

  return (
    <Modal id="update-expense-for-bva-modal" isOpen={!!updateExpenseForBva} title={expenseTitle} size="sm">
      <div data-testid="update-expense-for-bva-modal-container" className="mt-2 w-full">
        <div className="w-full h-[205px] flex flex-col border border-neutral-50 rounded-lg mb-3 py-3">
          <div className="flex flex-row justify-between items-center mb-2 px-1">
            <div>
              <Select
                className="max-w-[165px] !py-0 !border-none !shadow-none !text-neutral-400"
                state={forecastAsOf}
                setState={setForecastAsOf}
                id="forecast-as-of-expense"
                placeholder="Select forecast"
                buttonIconColor="text-neutral-400"
              />
            </div>
            <div className="flex flex-row items-center gap-4 mr-2">
              <div className="flex flex-row items-center gap-2 pb-1">
                <div className="w-3.5 h-[2px] bg-orange" />
                <Typography color="empty">Actuals</Typography>
              </div>
              <div className="flex flex-row items-center gap-2 pb-1">
                <div className="w-3.5 h-[2px] bg-neutral-200" />
                <Typography color="empty">Forecast</Typography>
              </div>
            </div>
          </div>
          <BvaGraph
            forecast={forecastValues}
            actuals={reviewForExpense?.actualValues ?? []}
            xAxisPadding={{ left: 48, right: 12 }}
          />
        </div>
        <DisplayExpenseAverages actualAverageValues={reviewForExpense?.actualAverageValues ?? []} />
        <ExpenseForm
          onClose={() => {
            setTimeout(() => {
              formState.resetFormState();
              setUpdateExpenseForBva(undefined);
              setFormula([]);
              setVariables({});
              setUpdatedFormula([]);
            }, 200);
          }}
          formState={formState}
          variables={variables}
          setVariables={setVariables}
          displayFormulaError={displayFormulaError}
          setDisplayFormulaError={setDisplayFormulaError}
          formula={formula}
          setFormula={setFormula}
          updatedFormula={updatedFormula}
          setUpdatedFormula={setUpdatedFormula}
          formulaValue={formulaValue}
          setFormulaValue={setFormulaValue}
          formulaList={formulaList}
          completeReview={completeReviewForExpense}
          createExpense={async () => {
            if (!updateExpenseForBva) return;
            await updateExpense({
              organizationUuid,
              expenseUuid: updateExpenseForBva.expenseUuid,
              formulaUuid: updateExpenseForBva.formulaUuid,
              type:
                formState.type === 'headcountDriven'
                  ? formState.headcountDriverType.selected?.value ?? null
                  : formState.type,
              category: formState.category.selected?.value ?? null,
              frequency: formState.frequency.selected?.value ?? null,
              amount:
                formState.headcountDriverType.selected?.value === 'headcountPercentCompensation'
                  ? formState.percentage.value
                  : formState.amount.value,
              updatedFormula,
              variables,
              formulaList,
              setDisplayFormulaError,
              employmentTypes: formState.employmentTypes.selected
                ? formState.employmentTypes.selected
                    .map((employmentType) => employmentType.value)
                    .filter((value): value is string => value !== null && value !== undefined)
                : ['all'],
              successCallback: ({ createdExpense }: { createdExpense: IExpense }) => {
                refreshData();
                setUpdateExpenseForBva(undefined);
                const expenseIndex = expenses.findIndex((expense) => expense.uuid === updateExpenseForBva.uuid);
                if (expenseIndex !== -1) {
                  const updatedExpenses = [...expenses];
                  updatedExpenses[expenseIndex] = createdExpense;
                  setExpenses(updatedExpenses);
                } else {
                  reload();
                }
                completeReviewForExpense();
                revalidateFilteredExpensesReport();
                revalidateFormulaList();
                setFormula([]);
                setVariables({});
                setUpdatedFormula([]);
                setFormulaValue('');
                setDisplayFormulaError({ isDisplayed: false, message: '' });
                formState.resetFormState();
                toast.success('Expense updated');
              },
              failureCallback: () => {
                toast.error('Failed to update expense');
              },
              validateFormState: (): boolean => {
                return formState.validateFormState({ expenses, formulas: formulaList });
              },
            });
          }}
        />
      </div>
    </Modal>
  );
};

export default UpdateExpenseForBvaModal;
